import type { ApiClient } from '../@sprinx/react-after-razzle';

export enum AdminStaticContentType {
  'document-to-download' = 'document-to-download',
  'footer-contact-box' = 'footer-contact-box',
  'footer-headquarters' = 'footer-headquarters',
  'homepage-carousel' = 'homepage-carousel',
  'homepage-image-section' = 'homepage-image-section',
  'homepage-main-image' = 'homepage-main-image',
  'homepage-main-info-box' = 'homepage-main-info-box',
  'homepage-secondary-info-box' = 'homepage-secondary-info-box',
  'homepage-uder-carousel-left-image' = 'homepage-uder-carousel-left-image',
  'homepage-uder-carousel-right-image' = 'homepage-uder-carousel-right-image',
  'static-page' = 'static-page',
  'static-pages-order' = 'static-pages-order',
}

export type AdminStaticPageListItemDto = {
  group: string;
  hidden?: boolean;
  locales: string[];
  orderIndex: number;
  title: string;
};

export type AdminStaticPageListDto = {
  items: AdminStaticPageListItemDto[];
  order: string[];
};

export enum AdminContactCountry {
  cz = 'cz',
  de_at = 'de_at',
  hu = 'hu',
  other = 'other',
  sk = 'sk',
}

export type AdminContactInfo = {
  _id: string;
  _realm: string;
  country: AdminContactCountry;
  email: string;
  gender: string;
  name: string;
  phone: string;
  priority: number | undefined;
  type: AdminContactInfoType;
};

export enum AdminContactInfoType {
  'contact-info' = 'contact-info',
}

export type AdminHeadquarters = {
  _id: string;
  _realm: string;
  address1: string;
  address2: string;
  email: string;
  fax: string;
  location: AdminHeadquartersLocation;
  phone: string;
  type: AdminHeadquartersType;
};

export enum AdminHeadquartersType {
  headquarters = 'headquarters',
}

export enum AdminHeadquartersLocation {
  'ceske-budejovice' = 'ceske-budejovice',
  'chotoviny' = 'chotoviny',
  'primary' = 'primary',
}

export enum AdminDepartmentInfoType {
  department = 'department',
}

export enum AdminDepartmentInfoKind {
  'department-complaint' = 'department-complaint',
  'department-orders' = 'department-orders',
  'department-warranty' = 'department-warranty',
  manager = 'manager',
}

export type AdminDepartmentInfo = {
  _id: string;
  _realm: string;
  email: string;
  kind: AdminDepartmentInfoKind;
  personName?: string;
  phone: string;
  type: AdminDepartmentInfoType;
};

export type AdminStaticContentDto = {
  _realm: string;
  body?: string;
  currency?: string;
  group?: string;
  hidden?: boolean;
  id: string;
  locales?: string[];
  path?: string;
  title?: string;
  type: AdminStaticContentType;
  updatedAt: string;
  updatedBy: string;
  url?: string;
};

export type AdminContactPageDataDto = {
  contactsInfo: AdminContactInfo[];
  departmentInfo: AdminDepartmentInfo[];
  headquarters: AdminHeadquarters[];
};

export const getAdminStaticContent = async (
  apiClient: ApiClient,
  type: AdminStaticContentType,
): Promise<AdminStaticContentDto[] | null> => {
  try {
    return await apiClient.get<AdminStaticContentDto[], {}>('/v1/config/by-type/:type', { type });
  } catch {
    return null;
  }
};

export const getOneAdminStaticContent = async (
  apiClient: ApiClient,
  type: AdminStaticContentType,
): Promise<AdminStaticContentDto | null> => {
  try {
    return await apiClient.get<AdminStaticContentDto, {}>('/v1/config/one-by-type/:type', { type });
  } catch {
    return null;
  }
};

export const getAdminStaticPage = async (
  apiClient: ApiClient,
  options: { group: string },
): Promise<AdminStaticContentDto[]> => {
  const { group } = options;
  const data = await apiClient.get<AdminStaticContentDto[], {}>('/v1/config/static-pages/:group', {
    group,
  });

  return data;
};

export const getAdminStaticPageList = async (apiClient: ApiClient, locale: string): Promise<AdminStaticPageListDto> => {
  const data = await apiClient.get<AdminStaticPageListDto, {}>('/v1/config/static-pages-list/:locale', { locale });
  return data;
};

export const getAdminContactPageData = async (apiClient: ApiClient): Promise<AdminContactPageDataDto> => {
  const data = await apiClient.get<AdminContactPageDataDto, {}>('/v1/config/contact-page-data');
  return data;
};
